import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useApi } from "../../utils/api";
import ConnectInstagramButton from "../CoachPortal/ConnectInstagramButton";
import InstagramFeed from "../CoachPortal/InstagramFeed";

const IG_RETURN = "/admin?tab=instagram";
const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

export default function MarketingInstagramTab({ userId }) {
  const { getCoachProfileMe, disconnectInstagram } = useApi();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState("");
  const isPlatformUser = userId === PLATFORM_OWNER_USER_ID;

  const loadProfile = React.useCallback(async () => {
    if (!userId || !isPlatformUser) return;
    setLoadingProfile(true);
    try {
      const p = (await getCoachProfileMe()) || {};
      setProfile(p);
    } catch {
      setProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  }, [getCoachProfileMe, userId, isPlatformUser]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleDisconnect = async () => {
    setDisconnectError("");
    setDisconnecting(true);
    try {
      await disconnectInstagram(userId);
      setDisconnectOpen(false);
      await loadProfile();
    } catch (e) {
      setDisconnectError(e?.message || "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  if (!isPlatformUser) {
    return (
      <Alert severity="error">You are not authorized to use this page.</Alert>
    );
  }

  if (loadingProfile) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading profile…
        </Typography>
      </Stack>
    );
  }

  const igHandle = profile?.instagramUsername?.replace(/^@/, "") || null;

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect the Trainer App Instagram account (@trainerxapp). The token is
        stored on your admin coach profile and powers the thumbnail strip on the
        public site footer via the instagram-service{" "}
        <code style={{ fontSize: "0.85em" }}>public-feed</code> endpoint.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Set Lambda env{" "}
        <code style={{ whiteSpace: "nowrap" }}>
          PUBLIC_INSTAGRAM_FEED_COACH_ID
        </code>{" "}
        to exactly this coach id so the home page can load posts anonymously:{" "}
        <strong style={{ wordBreak: "break-all" }}>{userId}</strong>
      </Alert>

      {igHandle ? (
        <Typography sx={{ mb: 1 }}>
          Connected as <strong>@{igHandle}</strong>
        </Typography>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No Instagram username on this profile yet. Use Connect Instagram
          (OAuth completes on Instagram, then updates this profile).
        </Typography>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <ConnectInstagramButton oauthReturnPath={IG_RETURN} />
        <Button
          variant="outlined"
          color="error"
          disabled={!userId || disconnecting}
          onClick={() => {
            setDisconnectError("");
            setDisconnectOpen(true);
          }}
        >
          Disconnect Instagram
        </Button>
      </Stack>

      {disconnectError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setDisconnectError("")}
        >
          {disconnectError}
        </Alert>
      ) : null}

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
        Preview (same feed as footer)
      </Typography>
      <InstagramFeed userId={userId} />

      <Dialog
        open={disconnectOpen}
        onClose={() => !disconnecting && setDisconnectOpen(false)}
      >
        <DialogTitle>Disconnect Instagram?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes Instagram from your admin coach profile and clears
            tokens. The footer feed will stop updating until you connect again
            (and Lambda env stays pointed at this user id).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisconnectOpen(false)}
            disabled={disconnecting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={disconnecting}
            onClick={handleDisconnect}
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
